// src/Informes/Components/Charts.js
import React, { useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ZoomIn, ZoomOut } from "lucide-react";

/* ------------------------------ */
/* HELPERS ULTRA DEFENSIVOS       */
/* ------------------------------ */

const isValidDate = (d) => {
  const date = new Date(d);
  return date instanceof Date && !isNaN(date);
};

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safeArray = (arr) => (Array.isArray(arr) ? arr : []);

const compareStringValues = (a, b) => {
  const left = a == null ? "" : String(a);
  const right = b == null ? "" : String(b);
  return left.localeCompare(right);
};

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#d32f2f",
  "#6a1b9a",
  "#f57c00",
  "#0288d1",
  "#c2185b",
  "#7b1fa2",
  "#388e3c",
  "#fbc02d",
  "#5d4037",
  "#0097a7",
  "#8d6e63",
  "#512da8",
  "#455a64",
];

/* ------------------------------ */
/* EMPTY STATE                    */
/* ------------------------------ */

const EmptyState = ({ text = "Sin datos disponibles" }) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9ca3af",
      fontSize: 14,
    }}
  >
    {text ?? "Sin datos disponibles"}
  </Box>
);

/* ------------------------------ */
/* SHARED CONTROL BUTTONS         */
/* ------------------------------ */

const CtrlBtn = ({ label, onClick, title }) => (
  <Box
    component="button"
    title={title}
    onClick={onClick}
    sx={{
      background: "rgba(245,158,11,0.12)",
      border: "1px solid rgba(245,158,11,0.4)",
      borderRadius: "7px",
      color: "#f59e0b",
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1,
      minWidth: 30,
      minHeight: 30,
      px: "8px",
      py: "5px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "rgba(245,158,11,0.2)",
        color: "#fbbf24",
        borderColor: "rgba(245,158,11,0.62)",
      },
    }}
  >
    {label}
  </Box>
);

const formatMonthYearLabel = (value) => {
  if (!value) return "Sin fecha";

  if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
    const [year, month] = value.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString("es-ES", {
      month: "long",
      year: "numeric",
    });
  }

  const date = new Date(value);
  if (!isValidDate(date)) return String(value);

  return date.toLocaleDateString("es-ES", {
    month: "long",
    year: "numeric",
  });
};

const getMonthKey = (value) => {
  if (!value) return "";

  // Direct string slice — no Date object, no timezone offset.
  if (typeof value === "string") {
    // Already "YYYY-MM"
    if (/^\d{4}-\d{2}$/.test(value)) return value;
    // "YYYY-MM-DD" or "YYYY-MM-DDThh:mm:ss…" — slice is safe and tz-free
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 7);
  }

  // Date object — use UTC methods so the day boundary matches the DB date
  if (value instanceof Date) {
    if (!isValidDate(value)) return "";
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  }

  const date = new Date(value);
  if (!isValidDate(date)) return "";
  return date.toISOString().slice(0, 7);
};

const LineChartSideList = ({
  rows = [],
  emptyText = "Sin datos disponibles",
}) => (
  <Box
    sx={{
      flex: { xs: "1 1 auto", lg: "0 0 260px" },
      width: { xs: "100%", lg: 260 },
      minWidth: 0,
      height: "100%",
      p: 1.25,
      borderRadius: 2,
      background: "rgba(15, 23, 42, 0.42)",
      border: "1px solid rgba(148, 163, 184, 0.1)",
      overflow: "hidden",
    }}
  >
    {!rows.length ? (
      <EmptyState text={emptyText} />
    ) : (
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.04)",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(148,163,184,0.28)",
            borderRadius: 4,
          },
        }}
      >
        {rows.map((row, index) => (
          <Box
            key={`${row.label}-${index}`}
            sx={{
              py: 0.9,
              borderBottom:
                index === rows.length - 1
                  ? "none"
                  : "1px solid rgba(148,163,184,0.08)",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                color: "#94a3b8",
                textTransform: "capitalize",
                mb: row.values?.length ? 0.75 : 0.2,
              }}
            >
              {row.label}
            </Typography>

            {row.values?.length ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.55 }}>
                {row.values.map((item, itemIndex) => (
                  <Box
                    key={`${item.label}-${itemIndex}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: item.color || "#64748b",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "#cbd5e1",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: item.color || "#e5e7eb",
                        flexShrink: 0,
                      }}
                    >
                      {safeNumber(item.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      background: row.color || "#64748b",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#e5e7eb",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row.secondaryLabel || "Valor"}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: row.color || "#e5e7eb",
                    flexShrink: 0,
                  }}
                >
                  {safeNumber(row.value)}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/* ------------------------------ */
/* LINE CHART - CASOS             */
/* ------------------------------ */

export const CasesLineChart = ({ casesGraph = [] }) => {
  const safeData = safeArray(casesGraph).filter(
    (item) => item?.date && isValidDate(item?.date),
  );

  const monthTicks = Array.from(
    new Set(
      safeData.map((item) =>
        new Date(item?.date ?? "").toLocaleString("default", {
          month: "long",
          year: "numeric",
        }),
      ),
    ),
  );

  const data = [
    {
      id: "Casos",
      data: safeData.map((item) => ({
        x: item?.date ?? "",
        y: safeNumber(item?.count),
      })),
    },
  ];

  const monthlyTotals = safeData.reduce((acc, item) => {
    const monthKey = getMonthKey(item?.date);
    if (!monthKey) return acc;

    acc[monthKey] = (acc[monthKey] || 0) + safeNumber(item?.count);
    return acc;
  }, {});

  const listRows = Object.entries(monthlyTotals)
    .sort((a, b) => compareStringValues(b[0], a[0]))
    .map(([monthKey, total]) => ({

      label: formatMonthYearLabel(monthKey),
      value: safeNumber(total),
      color: "#60a5fa",
      secondaryLabel: "Expedientes",
    }));

  const hasData = data?.[0]?.data?.length;

  return (
    <Paper
      sx={{
        p: 2,
        height: 450,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        boxShadow: "0 0 40px rgba(59,130,246,0.15)",
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            color: "#e5e7eb",
            fontWeight: 700,
            letterSpacing: 0.5,
          }}
        >
          Evolución del # expedientes dentro del rango de fechas
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "#94a3b8",
            display: "block",
            mt: 0.6,
            lineHeight: 1.5,
          }}
        >
          Cuenta cada expediente por fecha de alta dentro del filtro actual para
          ver la tendencia del volumen total en el tiempo.
        </Typography>
      </Box>

      <Box
        sx={{
          height: 350,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: { xs: 250, lg: "100%" },
            background: "rgba(15, 23, 42, 0.6)",
            borderRadius: 2,
            border: "1px solid rgba(148, 163, 184, 0.1)",
          }}
        >
          {!hasData ? (
            <EmptyState />
          ) : (
            <ResponsiveLine
              data={data}
              colors={() => "#60a5fa"}
              curve="catmullRom"
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                min: 0,
                max: "auto",
              }}
              margin={{ top: 20, right: 20, bottom: 70, left: 60 }}
              axisBottom={{
                tickRotation: -40,
                legend: "Mes",
                legendOffset: 45,
                legendPosition: "middle",
                tickValues: monthTicks?.map((month) => {
                  const match = safeData.find(
                    (d) =>
                      new Date(d?.date ?? "").toLocaleString("default", {
                        month: "long",
                        year: "numeric",
                      }) === month,
                  );
                  return match?.date ?? "";
                }),
                format: (value) => {
                  const date = new Date(value ?? "");
                  return isValidDate(date)
                    ? date.toLocaleString("default", { month: "short" })
                    : "";
                },
              }}
              axisLeft={{
                legend: "Cantidad de casos",
                legendOffset: -50,
                legendPosition: "middle",
              }}
              enableGridX={false}
              enableGridY={true}
              gridYValues={5}
              pointSize={6}
              pointBorderWidth={2}
              pointColor="#0f172a"
              pointBorderColor="#60a5fa"
              enableArea={true}
              areaOpacity={0.12}
              useMesh={true}
              theme={{
                text: { fill: "#cbd5e1" },
                axis: {
                  ticks: {
                    text: { fill: "#94a3b8" },
                    line: { stroke: "#334155" },
                  },
                  legend: {
                    text: { fill: "#e2e8f0", fontWeight: 600 },
                  },
                  domain: {
                    line: { stroke: "#334155" },
                  },
                },
                grid: {
                  line: {
                    stroke: "rgba(148,163,184,0.12)",
                    strokeDasharray: "4 6",
                  },
                },
              }}
              tooltip={({ point }) => {
                const date = new Date(point?.data?.x ?? new Date());
                return (
                  <div
                    style={{
                      background: "rgba(15,23,42,0.95)",
                      padding: 12,
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.2)",
                      color: "#fff",
                    }}
                  >
                    <div style={{ fontSize: 12, marginBottom: 6 }}>
                      {date.toLocaleDateString("es-CO", {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {point?.data?.y ?? 0} casos
                    </div>
                  </div>
                );
              }}
            />
          )}
        </Box>
        <LineChartSideList rows={listRows} />
      </Box>
    </Paper>
  );
};

/* ------------------------------ */
/* LINE CHART - CONTACTS          */
/* ------------------------------ */
// (Neon SaaS Dark Grid)
export const ContactsLineChart = ({ contactsGraph = [] }) => {
  const safe = safeArray(contactsGraph)
    .filter((item) => item?.fecha && isValidDate(item?.fecha))
    .map((item) => {
      const date = new Date(item.fecha);

      return {
        date,
        // keep original string for tz-safe month grouping
        monthKey: getMonthKey(item.fecha),
        Cliente: safeNumber(item.Cliente),
        Pte: safeNumber(item.Pte),
        Llamado: safeNumber(item.Llamado),
        Cancelado: safeNumber(item.Cancelado),
      };
    })
    .sort((a, b) => a.date - b.date);

  const [visible, setVisible] = React.useState({
    Cliente: true,
    Pte: true,
    Llamado: true,
    Cancelado: true,
  });

  const COLORS = {
    Cliente: "#22c55e",
    Pte: "#f59e0b",
    Llamado: "#3b82f6",
    Cancelado: "#ef4444",
  };

  const data = Object.keys(visible)
    .filter((key) => visible[key])
    .map((key) => ({
      id: key,
      data: safe.map((d) => ({ x: d.date, y: d[key] })),
    }));

  const monthlyGrouped = safe.reduce((acc, item) => {
    const monthKey = item?.monthKey;
    if (!monthKey) return acc;

    if (!acc[monthKey]) {
      acc[monthKey] = {
        Cliente: 0,
        Pte: 0,
        Llamado: 0,
        Cancelado: 0,
      };
    }

    acc[monthKey].Cliente += safeNumber(item?.Cliente);
    acc[monthKey].Pte += safeNumber(item?.Pte);
    acc[monthKey].Llamado += safeNumber(item?.Llamado);
    acc[monthKey].Cancelado += safeNumber(item?.Cancelado);

    return acc;
  }, {});

  const listRows = Object.entries(monthlyGrouped)
    .sort((a, b) => compareStringValues(b[0], a[0]))
    .map(([monthKey, totals]) => ({

      label: formatMonthYearLabel(monthKey),
      values: Object.keys(COLORS)
        .filter((key) => visible[key])
        .map((key) => ({
          label: key,
          value: safeNumber(totals?.[key]),
          color: COLORS[key],
        })),
    }));

  const hasData = data.some((s) => s.data.length > 0);

  return (
    <Paper
      sx={{
        p: 2,
        height: 450,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        boxShadow: "0 0 40px rgba(59,130,246,0.15)",
      }}
    >
      {/* HEADER */}
      <Typography
        sx={{
          mb: 1.5,
          color: "#e5e7eb",
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        Evolución de contactos por estado
      </Typography>

      {/* CHART */}
      <Box
        sx={{
          height: 320,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: { xs: 240, lg: "100%" },
            background: "rgba(15, 23, 42, 0.6)",
            borderRadius: 2,
            border: "1px solid rgba(148, 163, 184, 0.1)",
          }}
        >
          {!hasData ? (
            <EmptyState />
          ) : (
            <ResponsiveLine
              data={data}
              curve="catmullRom"
              xScale={{
                type: "time",
                format: "native",
                precision: "day",
                useUTC: false,
              }}
              xFormat="time:%Y-%m-%d"
              yScale={{
                type: "linear",
                min: 0,
                max: "auto",
              }}
              axisBottom={{
                format: "%d %b",
                tickValues: "every 2 weeks",
                tickRotation: -40,
                tickPadding: 10,
                tickSize: 6,
                legend: "Fecha (día/mes)",
                legendOffset: 50,
                legendPosition: "middle",
              }}
              axisLeft={{
                tickPadding: 8,
                tickSize: 5,
                legend: "Total de contactos por estado",
                legendOffset: -55,
                legendPosition: "middle",
              }}
              colors={(d) => COLORS[d.id] || "#999"}
              pointSize={5}
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              enableArea={true}
              areaOpacity={0.08}
              useMesh={true}
              enableGridX={false}
              enableGridY={true}
              theme={{
                text: { fill: "#cbd5e1", fontSize: 12 },
                axis: {
                  domain: {
                    line: { stroke: "#334155" },
                  },
                  ticks: {
                    line: { stroke: "#334155" },
                    text: { fill: "#94a3b8" },
                  },
                  legend: {
                    text: { fill: "#e2e8f0", fontWeight: 600 },
                  },
                },
                grid: {
                  line: {
                    stroke: "rgba(148,163,184,0.12)",
                    strokeDasharray: "4 6",
                  },
                },
                crosshair: {
                  line: {
                    stroke: "#60a5fa",
                    strokeOpacity: 0.4,
                  },
                },
              }}
            />
          )}
        </Box>
        <LineChartSideList rows={listRows} />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2,
          flexWrap: "wrap",
        }}
      >
        {Object.keys(visible).map((key) => (
          <Box
            key={key}
            onClick={() =>
              setVisible((prev) => ({ ...prev, [key]: !prev[key] }))
            }
            sx={{
              cursor: "pointer",
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              fontSize: 12,
              color: visible[key] ? COLORS[key] : "#64748b",
              border: `1px solid ${
                visible[key] ? COLORS[key] : "rgba(100,116,139,0.3)"
              }`,
              background: visible[key] ? `${COLORS[key]}10` : "transparent",
              transition: "all 0.2s ease",
              userSelect: "none",
            }}
          >
            ● {key}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export const ContactsActualStateBarChart = ({ contactsGraph = [] }) => {
  const data = safeArray(contactsGraph)
    .map((item) => ({
      status: Number(item?.status ?? 0),
      label: item?.label ?? "Pte",
      total: safeNumber(item?.total ?? item?.count ?? 0),
    }))
    .filter((item) => item?.label && [0, 1].includes(item.status))
    .sort((a, b) => a.status - b.status);

  const barColors = ["#f59e0b", "#3b82f6"];

  return (
    <Paper
      sx={{
        p: 2,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.05)",
        boxShadow: "0 0 24px rgba(167,139,250,0.08)",
        color: "#e5e7eb",
      }}
    >
      {/* NO BORRAR: esta vista sirve como referencia rápida de estado actual del pipeline, aunque no debe sustituir la tabla detallada ni la línea evolutiva. Se mantiene como contexto de negocio y no debe eliminarse. */}
      <Typography
        sx={{
          mb: 1.5,
          color: "#e5e7eb",
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        Estado actual: PTE vs. Llamado
      </Typography>

      {!data.length ? (
        <Box sx={{ height: 260 }}>
          <EmptyState />
        </Box>
      ) : (
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 10, right: 20, left: -20, bottom: 25 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(148,163,184,0.18)"
              />
              <XAxis
                dataKey="label"
                tick={{ fill: "#94a3b8", fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: "#334155" }}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: "#334155" }}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                wrapperStyle={{ color: "#e5e7eb" }}
                contentStyle={{
                  background: "rgba(15,23,42,0.98)",
                  border: "1px solid rgba(148,163,184,0.25)",
                  borderRadius: 10,
                  color: "#e5e7eb",
                  boxShadow: "0 12px 32px rgba(15,23,42,0.45)",
                }}
                labelStyle={{ color: "#f8fafc", fontWeight: 700 }}
                itemStyle={{ color: "#e5e7eb", fontWeight: 500 }}
                formatter={(value, name) => [Number(value), name || "Total"]}
                labelFormatter={(label) => `Estado: ${label}`}
              />
              <Legend wrapperStyle={{ color: "#e5e7eb", fontSize: 12 }} />
              <Bar
                dataKey="total"
                name="Total"
                radius={[8, 8, 0, 0]}
                maxBarSize={90}
                activeBar={{
                  fill: "rgba(255,255,255,0.14)",
                  stroke: "rgba(255,255,255,0.12)",
                }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`${entry.label}-${index}`}
                    fill={barColors[index % barColors.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};
/* ------------------------------ */
/* PIE CHART - CONTACTS           */
/* ------------------------------ */
export const ContactsPieChart = ({ contactsGraph = [] }) => {
  const [legendFs, setLegendFs] = React.useState(11);
  const data = useMemo(() => {
    const grouped = {};

    safeArray(contactsGraph).forEach((item) => {
      if (!item?.fecha || !isValidDate(item?.fecha)) return;

      const key = getMonthKey(item?.fecha ?? "");

      grouped[key] = (grouped[key] || 0) + safeNumber(item?.Cliente);
    });

    return Object.entries(grouped ?? {})
      .filter(([_, v]) => (v ?? 0) > 0)
      .sort((a, b) => (b?.[1] ?? 0) - (a?.[1] ?? 0))
      .map(([month, value], index) => {
        // Use local-time constructor (year, month-1, day) to avoid UTC-midnight
        // shifting the date into the previous month in negative-offset timezones.
        const [yr, mo] = month.split("-");
        const date = new Date(Number(yr), Number(mo) - 1, 1);

        const fullLabel = date.toLocaleDateString("es-CO", {
          month: "long",
          year: "numeric",
        });

        const shortLabel = date.toLocaleDateString("es-CO", {
          month: "short",
          year: "numeric",
        });

        return {
          id: fullLabel,
          label: shortLabel,
          value: value ?? 0,
          color: COLORS[index % COLORS.length],
        };
      });
  }, [contactsGraph]);

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
        boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        {/* NO BORRAR: esta vista representa la distribución por mes del filtro general y debe mantenerse como contexto de generación de clientes. No borrar ni la lógica ni este bloque de contexto. */}
        <Typography
          sx={{ color: "#e5e7eb", fontWeight: 700, letterSpacing: 0.5 }}
        >
          Generación de clientes por mes
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexShrink: 0,
            ml: 1,
          }}
        >
          <CtrlBtn
            label={<ZoomOut size={17} strokeWidth={2.5} />}
            title="Reducir texto"
            onClick={() => setLegendFs((s) => Math.max(8, s - 1))}
          />
          <Box
            component="span"
            sx={{
              fontSize: "14px",
              color: "#64748b",
              minWidth: 22,
              textAlign: "center",
            }}
          >
            {legendFs}
          </Box>
          <CtrlBtn
            label={<ZoomIn size={17} strokeWidth={2.5} />}
            title="Aumentar texto"
            onClick={() => setLegendFs((s) => Math.min(18, s + 1))}
          />
        </Box>
      </Box>

      {!data?.length ? (
        <Box sx={{ height: 200 }}>
          <EmptyState />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ flex: "0 0 210px", height: 210 }}>
            <ResponsivePie
              data={data}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.65}
              padAngle={1.2}
              cornerRadius={8}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.4]] }}
              colors={(d) => d?.data?.color ?? "#64748b"}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={20}
              arcLabelsTextColor="#ffffff"
              arcLabel={(d) => `${d?.value ?? 0}`}
              theme={{
                text: { fill: "#e5e7eb", fontSize: 11 },
                tooltip: {
                  container: {
                    backgroundColor: "rgba(15,23,42,0.95)",
                    color: "#fff",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.08)",
                  },
                },
              }}
              motionConfig="gentle"
              animate
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              maxHeight: 210,
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255,255,255,0.04)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.15)",
                borderRadius: 4,
              },
            }}
          >
            {data.map((item) => (
              <Box
                key={item.id}
                sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
              >
                <Box
                  sx={{
                    width: legendFs - 1,
                    height: legendFs - 1,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    flexShrink: 0,
                    mt: "2px",
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: legendFs,
                    color: "#cbd5e1",
                    flex: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {item.id}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: legendFs,
                    color: item.color,
                    fontWeight: 700,
                    flexShrink: 0,
                    ml: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.value}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

/* ------------------------------ */
/* PIE CHART - CASES              */
/* ------------------------------ */

export const CasesPieCharts = ({ casesStatusData = [] }) => {
  const [legendFs, setLegendFs] = React.useState(11);
  const data = useMemo(
    () =>
      safeArray(casesStatusData)
        .filter((i) => i?.label && i?.value != null)
        .map((i, idx) => ({
          id: i?.label ?? `item-${idx}`,
          value: safeNumber(i?.value),
          realValue: Number(i?.realValue ?? 0) || 0,
          color: COLORS?.[idx % COLORS.length],
        })),
    [casesStatusData],
  );

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
        boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}
        >
          <Typography
            sx={{ color: "#e5e7eb", fontWeight: 700, letterSpacing: 0.5 }}
          >
            Distribución de expedientes por estado del flujo (%)
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#94a3b8",
              lineHeight: 1.5,
              display: "block",
            }}
          >
            Muestra el estado actual registrado en cada expediente. El
            porcentaje se calcula sobre el total de casos del filtro activo.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexShrink: 0,
            ml: 1,
          }}
        >
          <CtrlBtn
            label={<ZoomOut size={17} strokeWidth={2.5} />}
            title="Reducir texto"
            onClick={() => setLegendFs((s) => Math.max(8, s - 1))}
          />
          <Box
            component="span"
            sx={{
              fontSize: "14px",
              color: "#64748b",
              minWidth: 22,
              textAlign: "center",
            }}
          >
            {legendFs}
          </Box>
          <CtrlBtn
            label={<ZoomIn size={17} strokeWidth={2.5} />}
            title="Aumentar texto"
            onClick={() => setLegendFs((s) => Math.min(18, s + 1))}
          />
        </Box>
      </Box>

      {!data?.length ? (
        <Box sx={{ height: 200 }}>
          <EmptyState />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ flex: "0 0 210px", height: 210 }}>
            <ResponsivePie
              data={data}
              valueFormat={(v) => `${v ?? 0}%`}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.62}
              padAngle={1.2}
              cornerRadius={8}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.4]] }}
              colors={(d) => d?.data?.color ?? "#64748b"}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={20}
              arcLabelsTextColor="#ffffff"
              arcLabel={(d) => `${d?.value ?? 0}%`}
              theme={{
                text: { fill: "#e5e7eb", fontSize: 11 },
                tooltip: {
                  container: {
                    backgroundColor: "rgba(15,23,42,0.95)",
                    color: "#fff",
                    borderRadius: 10,
                    border: "1px solid rgba(148,163,184,0.15)",
                  },
                },
              }}
              tooltip={({ datum }) => (
                <div
                  style={{
                    background: "rgba(15,23,42,0.97)",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${datum.color}40`,
                    color: "#fff",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: datum.color,
                      marginBottom: 4,
                    }}
                  >
                    {datum.id}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>
                    {datum.value}%
                    {datum.data?.realValue > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          marginLeft: 6,
                        }}
                      >
                        ({datum.data.realValue} exp.)
                      </span>
                    )}
                  </div>
                </div>
              )}
              motionConfig="gentle"
              animate
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              maxHeight: 210,
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255,255,255,0.04)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.15)",
                borderRadius: 4,
              },
            }}
          >
            {data.map((item) => (
              <Box
                key={item.id}
                sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
              >
                <Box
                  sx={{
                    width: legendFs - 1,
                    height: legendFs - 1,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    flexShrink: 0,
                    mt: "2px",
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: legendFs,
                    color: "#cbd5e1",
                    flex: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {item.id}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: legendFs,
                    color: item.color,
                    fontWeight: 700,
                    flexShrink: 0,
                    ml: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.value}%
                  {item.realValue > 0 && (
                    <Box
                      component="span"
                      sx={{ color: "#64748b", fontWeight: 400, ml: "3px" }}
                    >
                      ({item.realValue})
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

const taskStatusLegend = [
  { value: "0", label: "0 – Pendiente" },
  { value: "1", label: "1 – En curso" },
  { value: "2", label: "2 – Completado" },
  { value: "3", label: "3 – Cancelado" },
  { value: "4", label: "4 – Archivado" },
];

const darkNeonSelectMenuProps = {
  MenuProps: {
    PaperProps: {
      sx: {
        bgcolor: "#0f172a",
        color: "#e2e8f0",
        borderRadius: 2,
        border: "1px solid rgba(96,165,250,0.28)",
        boxShadow: "0 16px 32px rgba(15,23,42,0.5)",
        maxHeight: 260,
        overflow: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(96, 165, 250, 0.8) transparent",
        "&::-webkit-scrollbar": {
          width: 8,
          background: "transparent",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background:
            "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(59,130,246,0.55))",
          borderRadius: 999,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
          boxShadow: "0 0 12px rgba(96,165,250,0.35)",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background:
            "linear-gradient(180deg, rgba(125,211,252,0.98), rgba(59,130,246,0.7))",
        },
        "& .MuiMenuItem-root": {
          color: "#e2e8f0",
          fontSize: 13,
          minHeight: 36,
          paddingY: 0.75,
          "&:hover": { bgcolor: "rgba(96,165,250,0.18)" },
          "&.Mui-selected": {
            bgcolor: "rgba(96,165,250,0.22)",
            color: "#f8fafc",
          },
        },
      },
    },
    MenuListProps: {
      sx: {
        padding: 0,
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(96, 165, 250, 0.8) transparent",
        "&::-webkit-scrollbar": {
          width: 8,
          background: "transparent",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background:
            "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(59,130,246,0.55))",
          borderRadius: 999,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
          boxShadow: "0 0 12px rgba(96,165,250,0.35)",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background:
            "linear-gradient(180deg, rgba(125,211,252,0.98), rgba(59,130,246,0.7))",
        },
      },
    },
  },
};

export const CasesFlowStatusDistributionChart = ({ matrixData = [] }) => {
  const rows = safeArray(matrixData);

  const chartData = useMemo(() => {
    const summary = {};

    rows.forEach((row) => {
      const label = row?.flow_status || "Sin etapa";
      summary[label] = (summary[label] || 0) + safeNumber(row?.cases_count);
    });

    const total = Object.values(summary).reduce(
      (sum, value) => sum + safeNumber(value),
      0,
    );

    return Object.entries(summary)
      .map(([flow_status, cases_count]) => ({
        id: flow_status,
        label: flow_status,
        value: total > 0 ? Number(((cases_count / total) * 100).toFixed(1)) : 0,
        realValue: cases_count,
        color:
          COLORS[Object.keys(summary).indexOf(flow_status) % COLORS.length],
      }))
      .sort((a, b) => b.realValue - a.realValue)
      .slice(0, 8);
  }, [rows]);

  if (!chartData.length) {
    return null;
  }

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
        border: "1px solid rgba(255,255,255,0.05)",
        mb: 2,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: "100%",
        }}
      >
        <Typography sx={{ color: "#e5e7eb", fontWeight: 700 }}>
          Distribución de expedientes por estado del flujo (%)
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#94a3b8", lineHeight: 1.5 }}
        >
          Muestra qué porcentaje del total de expedientes corresponde a cada
          etapa actual del flujo, resumiendo la distribución global en el filtro
          activo.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flex: 1,
            minHeight: 220,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ flex: "0 0 230px", height: 220 }}>
            <ResponsivePie
              data={chartData}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.62}
              padAngle={1.2}
              cornerRadius={8}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.4]] }}
              colors={(d) => d?.data?.color ?? "#64748b"}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={18}
              arcLabelsTextColor="#ffffff"
              arcLabel={(d) => `${d?.data?.value ?? 0}%`}
              tooltip={({ datum }) => (
                <div
                  style={{
                    background: "rgba(15,23,42,0.97)",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${datum.color}40`,
                    color: "#fff",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: datum.color,
                      marginBottom: 4,
                    }}
                  >
                    {datum.id}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>
                    {datum.data?.value ?? 0}%
                    <span
                      style={{ fontSize: 11, color: "#94a3b8", marginLeft: 6 }}
                    >
                      ({datum.data?.realValue ?? 0} exp.)
                    </span>
                  </div>
                </div>
              )}
              theme={{
                text: { fill: "#e5e7eb", fontSize: 11 },
                tooltip: {
                  container: {
                    backgroundColor: "rgba(15,23,42,0.95)",
                    color: "#fff",
                    borderRadius: 10,
                    border: "1px solid rgba(148,163,184,0.15)",
                  },
                },
              }}
              motionConfig="gentle"
              animate
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              maxHeight: 220,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255,255,255,0.04)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.15)",
                borderRadius: 4,
              },
            }}
          >
            {chartData.map((item) => (
              <Box
                key={item.id}
                sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Box
                  component="span"
                  sx={{ color: "#cbd5e1", fontSize: 12, flex: 1, minWidth: 0 }}
                >
                  {item.label}
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: item.color,
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.value}%
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export const CasesFlowStatusSummaryChart = ({ matrixData = [] }) => {
  const rows = safeArray(matrixData);

  const chartData = useMemo(() => {
    const summary = {};

    rows.forEach((row) => {
      const label = row?.flow_status || "Sin etapa";
      summary[label] = (summary[label] || 0) + safeNumber(row?.cases_count);
    });

    return Object.entries(summary)
      .map(([flow_status, cases_count]) => ({
        flow_status,
        cases_count,
      }))
      .sort((a, b) => b.cases_count - a.cases_count)
      .slice(0, 8);
  }, [rows]);

  if (!chartData.length) {
    return null;
  }

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
        border: "1px solid rgba(255,255,255,0.05)",
        mb: 2,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: "100%",
        }}
      >
        <Typography sx={{ color: "#e5e7eb", fontWeight: 700 }}>
          Resumen visual por etapa del flujo
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#94a3b8", lineHeight: 1.5 }}
        >
          Compara el volumen de expedientes por etapa para detectar qué estados
          concentran más casos dentro del rango seleccionado.
        </Typography>

        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 12, left: 0, bottom: 40 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(148,163,184,0.18)"
              />
              <XAxis
                dataKey="flow_status"
                tick={{ fill: "#cbd5e1", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#334155" }}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#334155" }}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                formatter={(value) => [`${value} expedientes`, "Total"]}
                contentStyle={{
                  background: "rgba(15,23,42,0.97)",
                  border: "1px solid rgba(148,163,184,0.18)",
                  borderRadius: 10,
                  color: "#f8fafc",
                  boxShadow: "0 12px 32px rgba(15,23,42,0.45)",
                }}
                labelStyle={{ color: "#f8fafc", fontWeight: 700 }}
                itemStyle={{ color: "#e2e8f0", fontWeight: 500 }}
              />
              <Bar dataKey="cases_count" radius={[8, 8, 0, 0]} fill="#60a5fa" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
};

export const CasesFlowStatusMatrixTable = ({ matrixData = [] }) => {
  const rows = safeArray(matrixData);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(12);
  const [caseTypeFilter, setCaseTypeFilter] = useState("all");
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");

  const uniqueCaseTypes = useMemo(
    () =>
      [
        ...new Set(
          rows
            .map((row) => row?.case_type_label)
            .filter(
              (value) => value !== null && value !== undefined && value !== "",
            )
            .map((value) => String(value)),
        ),
      ].sort((a, b) => compareStringValues(a, b)),
    [rows],
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesType =
        caseTypeFilter === "all" ||
        String(row?.case_type_label ?? "") === String(caseTypeFilter);
      const matchesStatus =
        taskStatusFilter === "all" ||
        String(row?.task_status) === String(taskStatusFilter);

      return matchesType && matchesStatus;
    });
  }, [rows, caseTypeFilter, taskStatusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const visibleRows = filteredRows.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage,
  );

  React.useEffect(() => {
    setPage(1);
  }, [caseTypeFilter, taskStatusFilter]);

  if (!rows.length) {
    return (
      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Typography sx={{ color: "#e5e7eb", fontWeight: 700, mb: 2 }}>
          Estado de expedientes por etapa y fecha
        </Typography>
        <Box sx={{ height: 140 }}>
          <EmptyState />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ color: "#e5e7eb", fontWeight: 700 }}>
            Estado de expedientes por etapa y fecha
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#94a3b8", lineHeight: 1.5 }}
          >
            Agrupa cada expediente por la última etapa registrada en su
            historial dentro del rango y por mes; sirve para ver cómo avanza el
            flujo real en el tiempo.
          </Typography>
        </Box>
        <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
          {filteredRows.length} filas filtradas · {totalPages} páginas
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <TextField
          select
          size="small"
          label="Tipo de caso"
          value={caseTypeFilter}
          onChange={(e) => setCaseTypeFilter(e.target.value)}
          SelectProps={darkNeonSelectMenuProps}
          sx={{
            minWidth: 180,
            "& .MuiInputBase-root": {
              color: "#e5e7eb",
              background: "rgba(15,23,42,0.45)",
              borderRadius: "10px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(148,163,184,0.25)",
            },
            "& .MuiInputLabel-root": { color: "#cbd5e1" },
            "& .MuiSvgIcon-root": { color: "#e5e7eb" },
          }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {uniqueCaseTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Estado"
          value={taskStatusFilter}
          onChange={(e) => setTaskStatusFilter(e.target.value)}
          SelectProps={darkNeonSelectMenuProps}
          sx={{
            minWidth: 190,
            "& .MuiInputBase-root": {
              color: "#e5e7eb",
              background: "rgba(15,23,42,0.45)",
              borderRadius: "10px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(148,163,184,0.25)",
            },
            "& .MuiInputLabel-root": { color: "#cbd5e1" },
            "& .MuiSvgIcon-root": { color: "#e5e7eb" },
          }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {taskStatusLegend.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          background: "rgba(96,165,250,0.08)",
          border: "1px solid rgba(96,165,250,0.22)",
          color: "#dbeafe",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        <strong>Vista resumida:</strong> cuando se selecciona{" "}
        <strong>Todos</strong>, la gráfica muestra los{" "}
        <strong>8 estados de etapa</strong> con mayor número de expedientes
        dentro del filtro actual.
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CasesFlowStatusDistributionChart matrixData={filteredRows} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CasesFlowStatusSummaryChart matrixData={filteredRows} />
        </Box>
      </Box>

      <Box sx={{ overflowX: "auto", mt: 2 }}>
        <Table
          size="small"
          sx={{ minWidth: 760, color: "#e5e7eb", mt: 5, borderRadius: 2 }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#cbd5e1", fontWeight: 700 }}>
                Tipo
              </TableCell>
              <TableCell sx={{ color: "#cbd5e1", fontWeight: 700 }}>
                Etapa
              </TableCell>
              <TableCell sx={{ color: "#cbd5e1", fontWeight: 700 }}>
                Estado
              </TableCell>
              <TableCell sx={{ color: "#cbd5e1", fontWeight: 700 }}>
                Mes
              </TableCell>
              <TableCell
                sx={{ color: "#cbd5e1", fontWeight: 700, textAlign: "right" }}
              >
                Expedientes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow
                key={`${row.case_type}-${row.flow_status}-${row.task_status}-${row.month_key}-${index}`}
                sx={{ "& td": { borderColor: "rgba(148,163,184,0.12)" } }}
              >
                <TableCell sx={{ color: "#e5e7eb" }}>
                  {row.case_type_label || "Sin tipo"}
                </TableCell>
                <TableCell sx={{ color: "#e5e7eb" }}>
                  {row.flow_status || "Sin etapa"}
                </TableCell>
                <TableCell sx={{ color: "#e5e7eb" }}>
                  {row.task_status_label || `Estado ${row.task_status ?? "-"}`}
                </TableCell>
                <TableCell sx={{ color: "#cbd5e1" }}>
                  {row.month_label || row.month_key || "Sin fecha"}
                </TableCell>
                <TableCell
                  sx={{ color: "#e5e7eb", textAlign: "right", fontWeight: 700 }}
                >
                  {safeNumber(row.cases_count)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mt: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
          Página {safePage} de {totalPages}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            component="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            sx={{
              border: "1px solid rgba(148,163,184,0.25)",
              background:
                safePage <= 1
                  ? "rgba(148,163,184,0.08)"
                  : "rgba(59,130,246,0.12)",
              color: safePage <= 1 ? "#64748b" : "#dbeafe",
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              cursor: safePage <= 1 ? "default" : "pointer",
            }}
          >
            Anterior
          </Box>

          <Box
            component="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            sx={{
              border: "1px solid rgba(148,163,184,0.25)",
              background:
                safePage >= totalPages
                  ? "rgba(148,163,184,0.08)"
                  : "rgba(59,130,246,0.12)",
              color: safePage >= totalPages ? "#64748b" : "#dbeafe",
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              cursor: safePage >= totalPages ? "default" : "pointer",
            }}
          >
            Siguiente
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

/* ------------------------------ */
/* LINE CHART - CONTRACTS         */
/* ------------------------------ */

export const ContractsLineChart = ({ contractsGraph = [] }) => {
  const safeData = safeArray(contractsGraph).filter(
    (item) => item?.date && isValidDate(item?.date),
  );

  const monthTicks = Array.from(
    new Set(
      safeData.map((item) =>
        new Date(item?.date ?? "").toLocaleString("default", {
          month: "long",
          year: "numeric",
        }),
      ),
    ),
  );

  const data = [
    {
      id: "Contratos",
      data: safeData.map((item) => ({
        x: item?.date ?? "",
        y: safeNumber(item?.count),
      })),
    },
  ];

  const listRows = [...safeData]
    .sort((a, b) => {
      const monthA = getMonthKey(a?.date);
      const monthB = getMonthKey(b?.date);
      return compareStringValues(monthB, monthA);
    })
    .map((item) => ({
      label: formatMonthYearLabel(item?.date),
      value: safeNumber(item?.count),
      color: "#22c55e",
      secondaryLabel: "Firmas",
    }));

  const hasData = data?.[0]?.data?.length;

  return (
    <Paper
      sx={{
        p: 2,
        height: 350,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        boxShadow: "0 0 40px rgba(34,197,94,0.12)",
      }}
    >
      <Typography
        sx={{
          mb: 1.5,
          color: "#e5e7eb",
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        Evolución de contratos firmados dentro del rango de fechas
      </Typography>

      <Box
        sx={{
          height: 260,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: { xs: 200, lg: "100%" },
            background: "rgba(15, 23, 42, 0.6)",
            borderRadius: 2,
            border: "1px solid rgba(148, 163, 184, 0.1)",
          }}
        >
          {!hasData ? (
            <EmptyState />
          ) : (
            <ResponsiveLine
              data={data}
              colors={() => "#22c55e"}
              curve="catmullRom"
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: 0, max: "auto" }}
              margin={{ top: 20, right: 20, bottom: 70, left: 60 }}
              axisBottom={{
                tickRotation: -40,
                legend: "Mes",
                legendOffset: 45,
                legendPosition: "middle",
                tickValues: monthTicks?.map((month) => {
                  const match = safeData.find(
                    (d) =>
                      new Date(d?.date ?? "").toLocaleString("default", {
                        month: "long",
                        year: "numeric",
                      }) === month,
                  );
                  return match?.date ?? "";
                }),
                format: (value) => {
                  const date = new Date(value ?? "");
                  return isValidDate(date)
                    ? date.toLocaleString("default", { month: "short" })
                    : "";
                },
              }}
              axisLeft={{
                legend: "Contratos firmados",
                legendOffset: -50,
                legendPosition: "middle",
              }}
              enableGridX={false}
              enableGridY={true}
              gridYValues={5}
              pointSize={6}
              pointBorderWidth={2}
              pointColor="#0f172a"
              pointBorderColor="#22c55e"
              enableArea={true}
              areaOpacity={0.12}
              useMesh={true}
              theme={{
                text: { fill: "#cbd5e1" },
                axis: {
                  ticks: {
                    text: { fill: "#94a3b8" },
                    line: { stroke: "#334155" },
                  },
                  legend: { text: { fill: "#e2e8f0", fontWeight: 600 } },
                  domain: { line: { stroke: "#334155" } },
                },
                grid: {
                  line: {
                    stroke: "rgba(148,163,184,0.12)",
                    strokeDasharray: "4 6",
                  },
                },
              }}
              tooltip={({ point }) => {
                const date = new Date(point?.data?.x ?? new Date());
                return (
                  <div
                    style={{
                      background: "rgba(15,23,42,0.95)",
                      padding: 12,
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.2)",
                      color: "#fff",
                    }}
                  >
                    <div style={{ fontSize: 12, marginBottom: 6 }}>
                      {date.toLocaleDateString("es-CO", {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {point?.data?.y ?? 0} contratos
                    </div>
                  </div>
                );
              }}
            />
          )}
        </Box>
        <LineChartSideList rows={listRows} />
      </Box>
    </Paper>
  );
};

/* ------------------------------ */
/* PIE CHART - CONTRACTS          */
/* ------------------------------ */

export const ContractsPieChart = ({ contractsByTitle = [] }) => {
  const [legendFs, setLegendFs] = React.useState(11);
  const data = useMemo(() => {
    return safeArray(contractsByTitle)
      .filter((item) => item?.title && safeNumber(item?.total) > 0)
      .slice(0, 14)
      .map((item, index) => ({
        id: item.title,
        label: item.title,
        value: safeNumber(item.total),
        color: COLORS[index % COLORS.length],
      }));
  }, [contractsByTitle]);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        background: "linear-gradient(145deg, rgb(31,41,55), rgb(17,24,39))",
        boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          sx={{ color: "#e5e7eb", fontWeight: 700, letterSpacing: 0.5 }}
        >
          Distribución de firmas por tipo de contrato
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexShrink: 0,
            ml: 1,
          }}
        >
          <CtrlBtn
            label={<ZoomOut size={17} strokeWidth={2.5} />}
            title="Reducir texto"
            onClick={() => setLegendFs((s) => Math.max(8, s - 1))}
          />
          <Box
            component="span"
            sx={{
              fontSize: "14px",
              color: "#64748b",
              minWidth: 22,
              textAlign: "center",
            }}
          >
            {legendFs}
          </Box>
          <CtrlBtn
            label={<ZoomIn size={17} strokeWidth={2.5} />}
            title="Aumentar texto"
            onClick={() => setLegendFs((s) => Math.min(18, s + 1))}
          />
        </Box>
      </Box>

      {!data?.length ? (
        <Box sx={{ height: 200 }}>
          <EmptyState />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          {/* Donut sin etiquetas externas */}
          <Box sx={{ flex: "0 0 210px", height: 210 }}>
            <ResponsivePie
              data={data}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.6}
              padAngle={1.2}
              cornerRadius={6}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.4]] }}
              colors={(d) => d?.data?.color ?? "#64748b"}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={20}
              arcLabelsTextColor="#ffffff"
              arcLabel={(d) => (d.value >= total * 0.06 ? `${d.value}` : "")}
              theme={{
                text: { fill: "#e5e7eb", fontSize: 11 },
                tooltip: {
                  container: {
                    backgroundColor: "rgba(15,23,42,0.95)",
                    color: "#fff",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.08)",
                  },
                },
              }}
              tooltip={({ datum }) => (
                <div
                  style={{
                    background: "rgba(15,23,42,0.97)",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${datum.color}40`,
                    color: "#fff",
                    maxWidth: 260,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: datum.color,
                      marginBottom: 4,
                    }}
                  >
                    {datum.id}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {datum.value}{" "}
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>
                      (
                      {total > 0 ? ((datum.value / total) * 100).toFixed(1) : 0}
                      %)
                    </span>
                  </div>
                </div>
              )}
              motionConfig="gentle"
              animate
            />
          </Box>

          {/* Leyenda lateral con nombres completos */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              maxHeight: 210,
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255,255,255,0.04)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.15)",
                borderRadius: 4,
              },
            }}
          >
            {data.map((item) => (
              <Box
                key={item.id}
                sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
              >
                <Box
                  sx={{
                    width: legendFs - 1,
                    height: legendFs - 1,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    flexShrink: 0,
                    mt: "2px",
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: legendFs,
                    color: "#cbd5e1",
                    flex: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {item.label}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: legendFs,
                    color: item.color,
                    fontWeight: 700,
                    flexShrink: 0,
                    ml: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.value}
                  <Box
                    component="span"
                    sx={{ color: "#64748b", fontWeight: 400, ml: "3px" }}
                  >
                    ({total > 0 ? ((item.value / total) * 100).toFixed(1) : 0}
                    %)
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

/* ----------------------------------------------- */
/* LINE CHART - TASA DE CONVERSIÓN MENSUAL          */
/* ----------------------------------------------- */

export const ContactsConversionLineChart = ({ contactsGraph = [] }) => {
  /* kept as alias so old imports don't crash */
  return <ContactsConversionBarChart contactsGraph={contactsGraph} />;
};

/* ----------------------------------------------- */
/* STACKED BAR CHART - ESTADO POR MES               */
/* ----------------------------------------------- */

export const ContactsStatusStackedBarChart = ({ contactsGraph = [] }) => {
  const data = useMemo(() => {
    const map = {};

    safeArray(contactsGraph).forEach((item) => {
      if (!item?.fecha || !isValidDate(item?.fecha)) return;
      const month = getMonthKey(item.fecha);
      if (!month) return;

      if (!map[month]) {
        map[month] = {
          month,
          label: `${month.slice(5)}/${month.slice(2, 4)}`,
          Pte: 0,
          Llamado: 0,
          Cliente: 0,
          Cancelado: 0,
        };
      }

      map[month].Pte += safeNumber(item.Pte);
      map[month].Llamado += safeNumber(item.Llamado);
      map[month].Cliente += safeNumber(item.Cliente);
      map[month].Cancelado += safeNumber(item.Cancelado);
    });

    return Object.entries(map)
      .sort(([a], [b]) => compareStringValues(a, b))
      .map(([month, values]) => ({

        month,
        label: values.label,
        Pte: values.Pte,
        Llamado: values.Llamado,
        Cliente: values.Cliente,
        Cancelado: values.Cancelado,
      }));
  }, [contactsGraph]);

  return (
    <Paper
      sx={{
        p: 2,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.05)",
        boxShadow: "0 0 24px rgba(167,139,250,0.08)",
      }}
    >
      {/* NO BORRAR: este gráfico aporta el contexto mensual real del pipeline y complementa la línea y la conversión sin sustituirlas. */}
      <Typography
        sx={{
          color: "#e5e7eb",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: 0.5,
          mb: 1.5,
        }}
      >
        Estado por mes (PTE / Llamado / Cliente / Cancelado)
      </Typography>

      {data.length === 0 ? (
        <Box sx={{ height: 240 }}>
          <EmptyState />
        </Box>
      ) : (
        <Box sx={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 10, right: 18, left: -10, bottom: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(148,163,184,0.18)"
              />
              <XAxis
                dataKey="label"
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#334155" }}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#334155" }}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                contentStyle={{
                  background: "rgba(15,23,42,0.98)",
                  border: "1px solid rgba(148,163,184,0.25)",
                  borderRadius: 10,
                  color: "#e5e7eb",
                  boxShadow: "0 12px 32px rgba(15,23,42,0.45)",
                }}
                labelStyle={{ color: "#f8fafc", fontWeight: 700 }}
                itemStyle={{ color: "#e2e8f0", fontWeight: 500 }}
                formatter={(value, name) => [Number(value), name || "Total"]}
              />
              <Legend wrapperStyle={{ color: "#e5e7eb", fontSize: 12 }} />
              <Bar
                dataKey="Pte"
                stackId="stack"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="Llamado"
                stackId="stack"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="Cliente"
                stackId="stack"
                fill="#22c55e"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="Cancelado"
                stackId="stack"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

/* ----------------------------------------------- */
/* BAR CHART - TASA CONVERSIÓN (CSS puro)           */
/* ----------------------------------------------- */

export const ContactsConversionBarChart = ({ contactsGraph = [] }) => {
  const [zoom, setZoom] = React.useState(1);
  const monthly = useMemo(() => {
    const map = {};
    safeArray(contactsGraph).forEach((item) => {
      if (!item?.fecha || !isValidDate(item?.fecha)) return;
      const month = getMonthKey(item.fecha);
      if (!month) return;
      if (!map[month]) map[month] = { Cliente: 0, Total: 0 };
      map[month].Cliente += safeNumber(item.Cliente);
      map[month].Total +=
        safeNumber(item.Cliente) +
        safeNumber(item.Pte) +
        safeNumber(item.Llamado) +
        safeNumber(item.Cancelado);
    });
    return Object.entries(map)
      .sort(([a], [b]) => compareStringValues(a, b))
      .map(([month, v]) => ({

        month,
        label: `${month.slice(5)}/${month.slice(2, 4)}`,
        pct: v.Total > 0 ? Number(((v.Cliente / v.Total) * 100).toFixed(1)) : 0,
        clientes: v.Cliente,
        total: v.Total,
      }));
  }, [contactsGraph]);

  const maxPct = Math.max(...monthly.map((m) => m.pct), 1);

  return (
    <Paper
      sx={{
        p: 2,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.05)",
        boxShadow: "0 0 24px rgba(167,139,250,0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "#e5e7eb",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.5,
          }}
        >
          % Conversión mensual (contacto → cliente)
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexShrink: 0,
            ml: 1,
          }}
        >
          <CtrlBtn
            label="−"
            title="Reducir zoom"
            onClick={() =>
              setZoom((z) => Math.max(0.4, parseFloat((z - 0.2).toFixed(1))))
            }
          />
          <Box
            component="span"
            sx={{
              fontSize: "14px",
              color: "#64748b",
              minWidth: 32,
              textAlign: "center",
            }}
          >
            {Math.round(zoom * 100)}%
          </Box>
          <CtrlBtn
            label="+"
            title="Aumentar zoom"
            onClick={() =>
              setZoom((z) => Math.min(4, parseFloat((z + 0.2).toFixed(1))))
            }
          />
        </Box>
      </Box>

      {monthly.length === 0 ? (
        <Box sx={{ height: 160 }}>
          <EmptyState />
        </Box>
      ) : (
        <Box
          sx={{
            height: Math.max(170, Math.round(170 * zoom)),
            pt: 2,
            overflowX: "auto",
            overflowY: "hidden",
            transition: "height 0.2s ease",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255,255,255,0.04)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(167,139,250,0.3)",
              borderRadius: 4,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: "4px",
              minWidth: "100%",
              height: "100%",
            }}
          >
            {monthly.map(({ month, label, pct, clientes, total }) => (
              <Box
                key={month}
                title={`${month}: ${pct}% — ${clientes} de ${total} contactos`}
                sx={{
                  flex: "1 0 28px",
                  maxWidth: 52,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "3px",
                  cursor: "default",
                  "&:hover .conv-bar": {
                    filter: "brightness(1.25)",
                    transform: "scaleY(1.03)",
                    transformOrigin: "bottom",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "9px",
                    color:
                      pct >= 50 ? "#22c55e" : pct >= 25 ? "#fbbf24" : "#a78bfa",
                    fontWeight: 700,
                    lineHeight: 1,
                    mb: "1px",
                    visibility: pct > 0 ? "visible" : "hidden",
                  }}
                >
                  {pct}%
                </Box>
                <Box
                  className="conv-bar"
                  sx={{
                    width: "100%",
                    height: `${Math.max((pct / maxPct) * 110 * zoom, 2)}px`,
                    background:
                      pct >= 50
                        ? "linear-gradient(180deg, #34d399 0%, #059669 100%)"
                        : pct >= 25
                          ? "linear-gradient(180deg, #fde68a 0%, #f59e0b 100%)"
                          : "linear-gradient(180deg, #c4b5fd 0%, #7c3aed 100%)",
                    borderRadius: "3px 3px 0 0",
                    transition: "all 0.25s ease",
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: "9px",
                    color: "#64748b",
                    mt: "2px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};
/* ----------------------------------------------- */
/* LINE CHART - CLIENTES NUEVOS POR MES             */
/* ----------------------------------------------- */

export const ClientsMonthlyLineChart = ({ contactsGraph = [] }) => {
  const data = useMemo(() => {
    const monthly = {};
    safeArray(contactsGraph).forEach((item) => {
      if (!item?.fecha || !isValidDate(item?.fecha)) return;
      const month = getMonthKey(item.fecha);
      if (!month) return;
      monthly[month] = (monthly[month] || 0) + safeNumber(item.Cliente);
    });

    return [
      {
        id: "Nuevos clientes",
        data: Object.entries(monthly)
          .sort(([a], [b]) => compareStringValues(a, b))
          .map(([month, count]) => ({ x: month, y: count })),
      },
    ];
  }, [contactsGraph]);

  const listRows = [...(data?.[0]?.data ?? [])]
    .sort((a, b) => compareStringValues(b?.x ?? "", a?.x ?? ""))
    .map((item) => ({

      label: formatMonthYearLabel(item?.x),
      value: safeNumber(item?.y),
      color: "#fbbf24",
      secondaryLabel: "Clientes",
    }));

  const hasData = data[0]?.data?.length > 0;

  return (
    <Paper
      sx={{
        p: 2,
        height: 320,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        boxShadow: "0 0 32px rgba(251,191,36,0.08)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Typography
        sx={{ mb: 1.5, color: "#e5e7eb", fontWeight: 700, letterSpacing: 0.5 }}
      >
        Nuevos clientes por mes
      </Typography>
      <Box
        sx={{
          height: 240,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: { xs: 180, lg: "100%" },
            background: "rgba(15,23,42,0.6)",
            borderRadius: 2,
            border: "1px solid rgba(148,163,184,0.1)",
          }}
        >
          {!hasData ? (
            <EmptyState />
          ) : (
            <ResponsiveLine
              data={data}
              colors={() => "#fbbf24"}
              curve="catmullRom"
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: 0, max: "auto" }}
              margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
              axisBottom={{
                tickRotation: -40,
                legend: "Mes",
                legendOffset: 46,
                legendPosition: "middle",
                format: (v) => {
                  const [y, m] = v.split("-");
                  return `${m}/${y?.slice(2)}`;
                },
              }}
              axisLeft={{
                legend: "Clientes nuevos",
                legendOffset: -50,
                legendPosition: "middle",
              }}
              enableGridX={false}
              gridYValues={5}
              pointSize={6}
              pointBorderWidth={2}
              pointColor="#0f172a"
              pointBorderColor="#fbbf24"
              enableArea={true}
              areaOpacity={0.1}
              useMesh={true}
              theme={{
                text: { fill: "#cbd5e1" },
                axis: {
                  ticks: {
                    text: { fill: "#94a3b8" },
                    line: { stroke: "#334155" },
                  },
                  legend: { text: { fill: "#e2e8f0", fontWeight: 600 } },
                  domain: { line: { stroke: "#334155" } },
                },
                grid: {
                  line: {
                    stroke: "rgba(148,163,184,0.12)",
                    strokeDasharray: "4 6",
                  },
                },
              }}
              tooltip={({ point }) => (
                <div
                  style={{
                    background: "rgba(15,23,42,0.95)",
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid rgba(251,191,36,0.3)",
                    color: "#fff",
                  }}
                >
                  <div
                    style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}
                  >
                    {point?.data?.x ?? ""}
                  </div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: "#fbbf24" }}
                  >
                    {point?.data?.y ?? 0} clientes
                  </div>
                </div>
              )}
            />
          )}
        </Box>
        <LineChartSideList rows={listRows} />
      </Box>
    </Paper>
  );
};

/* ----------------------------------------------- */
/* LINE CHART - EXPEDIENTES VS FIRMAS POR MES       */
/* ----------------------------------------------- */

export const CasesVsContractsLineChart = ({
  casesGraph = [],
  contractsGraph = [],
}) => {
  const SERIES_COLORS = useMemo(
    () => ({ Expedientes: "#60a5fa", Firmas: "#22c55e" }),
    [],
  );

  const [visible, setVisible] = React.useState({
    Expedientes: true,
    Firmas: true,
  });

  const data = useMemo(() => {
    const byMonth = (arr, dateKey) => {
      const map = {};
      safeArray(arr).forEach((item) => {
        if (!item?.[dateKey] || !isValidDate(item[dateKey])) return;
        const month = String(item[dateKey]).slice(0, 7);
        map[month] = (map[month] || 0) + safeNumber(item.count);
      });
      return map;
    };

    const casesMap = byMonth(casesGraph, "date");
    const contMap = byMonth(contractsGraph, "date");
    const allMonths = [
      ...new Set([...Object.keys(casesMap), ...Object.keys(contMap)]),
    ].sort();

    return [
      {
        id: "Expedientes",
        data: allMonths.map((m) => ({ x: m, y: casesMap[m] || 0 })),
      },
      {
        id: "Firmas",
        data: allMonths.map((m) => ({ x: m, y: contMap[m] || 0 })),
      },
    ].filter((s) => visible[s.id]);
  }, [casesGraph, contractsGraph, visible]);

  const listRows = useMemo(() => {
    const byMonth = {};

    safeArray(casesGraph).forEach((item) => {
      if (!item?.date || !isValidDate(item.date)) return;
      const month = String(item.date).slice(0, 7);
      if (!byMonth[month]) byMonth[month] = { Expedientes: 0, Firmas: 0 };
      byMonth[month].Expedientes += safeNumber(item.count);
    });

    safeArray(contractsGraph).forEach((item) => {
      if (!item?.date || !isValidDate(item.date)) return;
      const month = String(item.date).slice(0, 7);
      if (!byMonth[month]) byMonth[month] = { Expedientes: 0, Firmas: 0 };
      byMonth[month].Firmas += safeNumber(item.count);
    });

    return Object.entries(byMonth)
      .sort(([a], [b]) => compareStringValues(b, a))
      .map(([month, values]) => ({

        label: formatMonthYearLabel(month),
        values: Object.keys(SERIES_COLORS)
          .filter((key) => visible[key])
          .map((key) => ({
            label: key,
            value: safeNumber(values?.[key]),
            color: SERIES_COLORS[key],
          })),
      }));
  }, [casesGraph, contractsGraph, visible, SERIES_COLORS]);

  const hasData = data.some((s) => s.data.length > 0);

  return (
    <Paper
      sx={{
        p: 2,
        height: 360,
        background: "linear-gradient(145deg, #0f172a, #111827)",
        borderRadius: 3,
        boxShadow: "0 0 32px rgba(96,165,250,0.1)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Typography
        sx={{ mb: 1.5, color: "#e5e7eb", fontWeight: 700, letterSpacing: 0.5 }}
      >
        Expedientes abiertos vs Contratos firmados por mes
      </Typography>

      <Box
        sx={{
          height: 255,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: { xs: 190, lg: "100%" },
            background: "rgba(15,23,42,0.6)",
            borderRadius: 2,
            border: "1px solid rgba(148,163,184,0.1)",
          }}
        >
          {!hasData ? (
            <EmptyState />
          ) : (
            <ResponsiveLine
              data={data}
              curve="catmullRom"
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: 0, max: "auto" }}
              margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
              axisBottom={{
                tickRotation: -40,
                legend: "Mes",
                legendOffset: 46,
                legendPosition: "middle",
                format: (v) => {
                  const [y, m] = v.split("-");
                  return `${m}/${y?.slice(2)}`;
                },
              }}
              axisLeft={{
                legend: "Cantidad",
                legendOffset: -50,
                legendPosition: "middle",
              }}
              colors={(d) => SERIES_COLORS[d.id] || "#999"}
              enableGridX={false}
              gridYValues={5}
              pointSize={6}
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              pointColor="#0f172a"
              enableArea={true}
              areaOpacity={0.07}
              useMesh={true}
              theme={{
                text: { fill: "#cbd5e1" },
                axis: {
                  ticks: {
                    text: { fill: "#94a3b8" },
                    line: { stroke: "#334155" },
                  },
                  legend: { text: { fill: "#e2e8f0", fontWeight: 600 } },
                  domain: { line: { stroke: "#334155" } },
                },
                grid: {
                  line: {
                    stroke: "rgba(148,163,184,0.12)",
                    strokeDasharray: "4 6",
                  },
                },
              }}
              tooltip={({ point }) => {
                const serieId = point?.serieId ?? "";
                const serieColor = SERIES_COLORS[serieId] ?? "#94a3b8";
                return (
                  <div
                    style={{
                      background: "rgba(15,23,42,0.95)",
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: `1px solid ${serieColor}40`,
                      color: "#fff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#94a3b8",
                        marginBottom: 4,
                      }}
                    >
                      {point?.data?.x ?? ""}
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: serieColor,
                      }}
                    >
                      {point?.data?.y ?? 0} {serieId.toLowerCase()}
                    </div>
                  </div>
                );
              }}
            />
          )}
        </Box>
        <LineChartSideList rows={listRows} />
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
        {Object.keys(SERIES_COLORS).map((key) => (
          <Box
            key={key}
            onClick={() =>
              setVisible((prev) => ({ ...prev, [key]: !prev[key] }))
            }
            sx={{
              cursor: "pointer",
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              fontSize: 12,
              color: visible[key] ? SERIES_COLORS[key] : "#64748b",
              border: `1px solid ${
                visible[key] ? SERIES_COLORS[key] : "rgba(100,116,139,0.3)"
              }`,
              background: visible[key]
                ? `${SERIES_COLORS[key]}10`
                : "transparent",
              transition: "all 0.2s ease",
              userSelect: "none",
            }}
          >
            ● {key}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
